import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { withMedicalHistoryRecordFixture } from "@src/packages/medical-history/test-fixtures/medicalHistoryRecordFixture";
import { useQueuedPatientUpdate } from "@louise/patients/sync/usePatientSync";
import {
  buildDiseaseHistoryNavigationStack,
  getCategoryFromRoute,
} from "./buildDiseaseHistoryNavigationStack";
import { transformDiseaseHistoryStateToRequest } from "./transformDiseaseHistoryStateToRequest";
import type { DiseaseHistoryFlowRoute } from "./buildDiseaseHistoryNavigationStack";
import type {
  BreastTestType,
  BreastConditionType,
} from "./breast-testing/options";
import type { DiseaseCategoryType, DiseaseType } from "./diseases/options";
import { DISEASE_CATEGORY_OPTIONS } from "./diseases/options";
import type { CancerTreatmentType } from "./cancer/options";
import { useTranslations } from "@src/packages/i18n";

type UnknownRecord = Record<string, unknown>;
type PatientSex = "AFAB" | "AMAB" | null | undefined;
type DiseaseHistoryPatch = ReturnType<
  typeof transformDiseaseHistoryStateToRequest
> & {
  medicalHistory?: {
    id?: string;
  };
};

// ── Detail types ─────────────────────────────────────────

export interface BreastTestingDetail {
  id?: string;
  testType: BreastTestType;
  other?: string | null;
  testDate?: string | null;
  outcome?: string | null;
  notes?: string | null;
}

export interface BreastConditionDetail {
  id?: string;
  condition: BreastConditionType;
  other?: string | null;
}

export interface DiseaseDetail {
  id?: string;
  category: DiseaseCategoryType;
  disease: DiseaseType;
  other?: string | null;
  diagnosisDate?: string | null;
  treatment?: string | null;
}

export interface CancerTreatmentDetail {
  id?: string;
  treatmentType: CancerTreatmentType;
  other?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
}

export interface CancerDetail {
  id?: string;
  cancer?: string | null;
  diagnosisDate?: string | null;
  inRemission?: string | null;
  treatments: CancerTreatmentDetail[];
  treatmentIdsToDelete: string[];
}

// ── State ────────────────────────────────────────────────

export interface DiseaseHistoryState {
  // Breast Testing
  hasBreastTestings: boolean | null;
  breastTestings: BreastTestingDetail[];
  breastTestingIdsToDelete: string[];
  // Breast Conditions
  hasBreastConditions: boolean | null;
  breastConditions: BreastConditionDetail[];
  breastConditionIdsToDelete: string[];
  // Disease Categories
  diseaseCategoryFlags: Record<DiseaseCategoryType, boolean | null>;
  diseases: DiseaseDetail[];
  diseaseIdsToDelete: string[];
  // Cancer
  hasCancerHistory: boolean | null;
  cancers: CancerDetail[];
  cancerIdsToDelete: string[];
  // Navigation
  navigationStack: DiseaseHistoryFlowRoute[];
  currentNavigationIndex: number;
}

function makeInitialCategoryFlags(): Record<
  DiseaseCategoryType,
  boolean | null
> {
  const flags = {} as Record<DiseaseCategoryType, boolean | null>;
  for (const cat of DISEASE_CATEGORY_OPTIONS) {
    flags[cat.id] = null;
  }
  return flags;
}

const initialState: DiseaseHistoryState = {
  hasBreastTestings: null,
  breastTestings: [],
  breastTestingIdsToDelete: [],
  hasBreastConditions: null,
  breastConditions: [],
  breastConditionIdsToDelete: [],
  diseaseCategoryFlags: makeInitialCategoryFlags(),
  diseases: [],
  diseaseIdsToDelete: [],
  hasCancerHistory: null,
  cancers: [],
  cancerIdsToDelete: [],
  navigationStack: ["BreastTesting"],
  currentNavigationIndex: 0,
};

// ── Breast imaging categories in the API ─────────────────

const BREAST_IMAGING_CATEGORIES = new Set([
  "mammogram",
  "breast_ultrasound",
  "breast_tomosynthesis",
  "breast_mri",
]);

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function asRecordArray(value: unknown): UnknownRecord[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function getString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function getBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function getPatientSex(patient: unknown): PatientSex {
  if (!isRecord(patient)) return null;
  return patient.gender === "AFAB" || patient.gender === "AMAB"
    ? patient.gender
    : null;
}

function getMedicalHistory(patient: unknown): UnknownRecord | null {
  if (!isRecord(patient)) return null;
  return isRecord(patient.medicalHistory) ? patient.medicalHistory : null;
}

function getBreastConditions(medicalHistory: unknown): BreastConditionDetail[] {
  if (!isRecord(medicalHistory)) return [];
  return asRecordArray(medicalHistory.breastConditions).map((condition) => ({
    id: getString(condition.id) ?? undefined,
    condition:
      condition.condition === "discharge" ||
      condition.condition === "pain" ||
      condition.condition === "other"
        ? condition.condition
        : "other",
    other: getString(condition.other),
  }));
}

/**
 * Extract breast testing entries from the patient's testResults array.
 * Breast tests are stored as fertility test records with breast-specific categories.
 */
function extractBreastTestings(patient: unknown): BreastTestingDetail[] {
  if (!isRecord(patient)) return [];
  const fertilityHistory = isRecord(patient.fertilityHistory)
    ? patient.fertilityHistory
    : null;
  const testResults = asRecordArray(
    fertilityHistory?.testResults ?? patient.testResults,
  );

  return testResults
    .filter((test) => {
      const imagingTestCategory = getString(test.imagingTestCategory);
      return (
        (test.type === "imaging" &&
          imagingTestCategory != null &&
          BREAST_IMAGING_CATEGORIES.has(imagingTestCategory)) ||
        (test.type === "clinical" &&
          test.clinicalTestCategory === "breast_examination")
      );
    })
    .map((test): BreastTestingDetail => {
      let testType: BreastTestType;
      if (
        test.type === "clinical" &&
        test.clinicalTestCategory === "breast_examination"
      ) {
        testType = "clinical_breast_exam";
      } else {
        const imagingTestCategory = getString(test.imagingTestCategory);
        testType = BREAST_IMAGING_CATEGORIES.has(imagingTestCategory ?? "")
          ? (imagingTestCategory as BreastTestType)
          : "other";
      }

      return {
        id: getString(test.id) ?? undefined,
        testType,
        other: getString(test.name),
        testDate: getString(test.date),
        outcome: getString(test.patientValue),
        notes: getString(test.notes),
      };
    });
}

/** Map API category string to our DiseaseCategoryType. */
const API_CATEGORY_MAP: Record<string, DiseaseCategoryType> = {
  endocrine: "endocrine",
  infectious: "infectious",
  cardiovascular: "cardiovascular",
  respiratory: "respiratory",
  gastrointestinal: "gastrointestinal",
  hepatic_biliary: "hepatic_biliary",
  renal_urinary: "renal_urinary",
  musculoskeletal: "musculoskeletal",
  hematological: "hematological",
  autoimmune: "autoimmune",
  genetic: "genetic",
  eating_disorders: "eating_disorders",
  neurological_mental_health: "neurological_mental_health",
};

/** Map API flag names to our DiseaseCategoryType. */
const FLAG_TO_CATEGORY: Record<string, DiseaseCategoryType> = {
  hasEndocrineDiseases: "endocrine",
  hasInfectiousDiseases: "infectious",
  hasCardiovascularDiseases: "cardiovascular",
  hasRespiratoryDiseases: "respiratory",
  hasGastrointestinalDiseases: "gastrointestinal",
  hasHepaticBiliaryDiseases: "hepatic_biliary",
  hasRenalUrinaryDiseases: "renal_urinary",
  hasMusculoskeletalDiseases: "musculoskeletal",
  hasHaematologicalDiseases: "hematological",
  hasAutoimmuneDiseases: "autoimmune",
  hasGeneticDiseases: "genetic",
  hasEatingDisorders: "eating_disorders",
  hasNeurologicalMentalHealthDiseases: "neurological_mental_health",
};

function extractDiseases(medicalHistory: unknown): DiseaseDetail[] {
  if (!isRecord(medicalHistory)) return [];
  const diseases = asRecordArray(medicalHistory.diseases);
  return diseases
    .filter((disease) => {
      const category = getString(disease.category);
      return category != null && API_CATEGORY_MAP[category];
    })
    .map((disease): DiseaseDetail => ({
      id: getString(disease.id) ?? undefined,
      category: API_CATEGORY_MAP[getString(disease.category) ?? ""]!,
      disease: (getString(disease.disease) as DiseaseType | null) ?? "other",
      other: getString(disease.other),
      diagnosisDate: getString(disease.diagnosisDate),
      treatment: getString(disease.treatmentDescription),
    }));
}

function extractCategoryFlags(
  medicalHistory: unknown,
): Record<DiseaseCategoryType, boolean | null> {
  const flags = makeInitialCategoryFlags();
  if (!isRecord(medicalHistory)) return flags;
  for (const [flagName, category] of Object.entries(FLAG_TO_CATEGORY)) {
    const val = medicalHistory[flagName];
    if (typeof val === "boolean") {
      flags[category] = val;
    }
  }
  return flags;
}

function extractCancers(medicalHistory: unknown): CancerDetail[] {
  if (!isRecord(medicalHistory)) return [];
  const cancers = asRecordArray(medicalHistory.cancers);
  return cancers.map((cancer): CancerDetail => ({
    id: getString(cancer.id) ?? undefined,
    cancer: getString(cancer.cancer),
    diagnosisDate: getString(cancer.diagnosisDate),
    inRemission: getString(cancer.inRemission),
    treatments: asRecordArray(cancer.treatments).map(
      (treatment): CancerTreatmentDetail => ({
        id: getString(treatment.id) ?? undefined,
        treatmentType:
          (getString(treatment.treatmentType) as CancerTreatmentType | null) ??
          "other",
        other: getString(treatment.other),
        startDate: getString(treatment.startDate),
        endDate: getString(treatment.endDate),
        notes: getString(treatment.notes),
      }),
    ),
    treatmentIdsToDelete: [],
  }));
}

// ── Resume route ─────────────────────────────────────────

export function getResumeRoute(patient: unknown): DiseaseHistoryFlowRoute {
  const sex = getPatientSex(patient);
  const isAMAB = sex === "AMAB";
  const defaultRoute: DiseaseHistoryFlowRoute = isAMAB
    ? "Disease_endocrine"
    : "BreastTesting";
  const mh = getMedicalHistory(patient);
  if (!mh) return defaultRoute;

  const breastConditions = getBreastConditions(mh);

  const breastTestings = extractBreastTestings(patient);
  const diseaseCategoryFlags = extractCategoryFlags(mh);
  const diseases = extractDiseases(mh);
  const cancers = extractCancers(mh);

  const tempState: DiseaseHistoryState = {
    ...initialState,
    hasBreastTestings: getBoolean(mh.hasBreastTestings),
    breastTestings,
    hasBreastConditions: getBoolean(mh.hasBreastConditions),
    breastConditions,
    diseaseCategoryFlags,
    diseases,
    hasCancerHistory: getBoolean(mh.hasCancerHistory),
    cancers,
  };

  const stack = buildDiseaseHistoryNavigationStack(tempState, sex);

  // If user has data from a later section, earlier sections are implicitly complete.
  // This guards against API flags being missing/undefined even though the user progressed.
  const pastBreastTesting =
    Object.values(diseaseCategoryFlags).some((v) => v !== null) ||
    diseases.length > 0 ||
    tempState.hasCancerHistory !== null;

  const pastDiseases =
    tempState.hasCancerHistory !== null || cancers.length > 0;

  const isRouteComplete = (route: DiseaseHistoryFlowRoute): boolean => {
    switch (route) {
      case "BreastTesting":
        return tempState.hasBreastTestings !== null || pastBreastTesting;
      case "BreastTestingDetails":
        return tempState.hasBreastTestings === true || pastBreastTesting;
      case "BreastConditions":
        return tempState.hasBreastConditions !== null || pastBreastTesting;
      case "BreastSymptomOther": {
        if (pastBreastTesting) return true;
        const other = tempState.breastConditions.find(
          (c) => c.condition === "other",
        );
        return other?.other != null && other.other.length > 0;
      }
      case "DiseaseDetails":
        return diseases.length > 0 || pastDiseases;
      case "CancerHistory":
        return tempState.hasCancerHistory !== null;
      case "CancerDetails":
        return (
          tempState.hasCancerHistory === true && tempState.cancers.length > 0
        );
      case "CancerTreatments":
        return tempState.cancers.some((c) => c.treatments.length > 0);
      case "CancerTreatmentDetails":
        return tempState.cancers.some((c) => c.treatments.length > 0);
      case "DiseaseHistoryComplete":
        return false;
      default: {
        // Disease_* category routes
        const category = getCategoryFromRoute(route);
        if (category) {
          return diseaseCategoryFlags[category] !== null || pastDiseases;
        }
        return false;
      }
    }
  };

  for (const route of stack) {
    if (route === "DiseaseHistoryComplete") return stack[0] ?? defaultRoute;
    if (!isRouteComplete(route)) return route;
  }

  return stack[0] ?? defaultRoute;
}

// ── Context ──────────────────────────────────────────────

interface DiseaseHistoryContextValue {
  state: DiseaseHistoryState;
  updateState: (updates: Partial<DiseaseHistoryState>) => void;
  resetState: () => void;
  save: () => Promise<void>;
}

const DiseaseHistoryContext = createContext<DiseaseHistoryContextValue | null>(
  null,
);

export function DiseaseHistoryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const t = useTranslations();
  const { data: rawPatient, isLoading } = usePatient();
  const patient = withMedicalHistoryRecordFixture(rawPatient);
  const { mutateAsync } = useQueuedPatientUpdate();
  const [state, setState] = useState<DiseaseHistoryState>(initialState);

  // Load data from patient
  useEffect(() => {
    if (isLoading || !patient) return;

    const medicalHistory = patient.medicalHistory;
    if (!medicalHistory) return;

    setState((prev) => {
      const breastConditions = getBreastConditions(medicalHistory);
      const breastTestings = extractBreastTestings(patient);
      const diseaseCategoryFlags = extractCategoryFlags(medicalHistory);
      const diseases = extractDiseases(medicalHistory);
      const cancers = extractCancers(medicalHistory);

      const next: Partial<DiseaseHistoryState> = {};

      if (
        prev.hasBreastTestings === null &&
        medicalHistory.hasBreastTestings !== undefined &&
        medicalHistory.hasBreastTestings !== null
      ) {
        next.hasBreastTestings = medicalHistory.hasBreastTestings;
      }
      if (prev.breastTestings.length === 0 && breastTestings.length > 0) {
        next.breastTestings = breastTestings;
      }
      if (
        prev.hasBreastConditions === null &&
        medicalHistory.hasBreastConditions !== undefined &&
        medicalHistory.hasBreastConditions !== null
      ) {
        next.hasBreastConditions = medicalHistory.hasBreastConditions;
      }
      if (prev.breastConditions.length === 0 && breastConditions.length > 0) {
        next.breastConditions = breastConditions;
      }

      const mergedCategoryFlags = { ...prev.diseaseCategoryFlags };
      let categoryFlagsChanged = false;
      for (const category of DISEASE_CATEGORY_OPTIONS) {
        const categoryId = category.id;
        if (
          mergedCategoryFlags[categoryId] === null &&
          diseaseCategoryFlags[categoryId] !== null
        ) {
          mergedCategoryFlags[categoryId] = diseaseCategoryFlags[categoryId];
          categoryFlagsChanged = true;
        }
      }
      if (categoryFlagsChanged) {
        next.diseaseCategoryFlags = mergedCategoryFlags;
      }

      if (prev.diseases.length === 0 && diseases.length > 0) {
        next.diseases = diseases;
      }
      if (
        prev.hasCancerHistory === null &&
        medicalHistory.hasCancerHistory !== undefined &&
        medicalHistory.hasCancerHistory !== null
      ) {
        next.hasCancerHistory = medicalHistory.hasCancerHistory;
      }
      if (prev.cancers.length === 0 && cancers.length > 0) {
        next.cancers = cancers;
      }

      if (Object.keys(next).length === 0) return prev;

      const merged: DiseaseHistoryState = {
        ...prev,
        ...next,
      };

      const sex = patient?.gender as "AFAB" | "AMAB" | null | undefined;
      const stack = buildDiseaseHistoryNavigationStack(merged, sex);

      return {
        ...merged,
        navigationStack: stack,
        currentNavigationIndex: Math.min(
          prev.currentNavigationIndex,
          Math.max(stack.length - 1, 0),
        ),
      };
    });
  }, [patient, isLoading]);

  const sex = patient?.gender as "AFAB" | "AMAB" | null | undefined;
  const updateState = useCallback(
    (updates: Partial<DiseaseHistoryState>) => {
      setState((prev) => {
        const newState = { ...prev, ...updates };
        const stack = buildDiseaseHistoryNavigationStack(newState, sex);
        return {
          ...newState,
          navigationStack: stack,
          currentNavigationIndex: Math.min(
            newState.currentNavigationIndex ?? 0,
            Math.max(stack.length - 1, 0),
          ),
        };
      });
    },
    [sex],
  );

  const save = useCallback(async () => {
    const payload = transformDiseaseHistoryStateToRequest(state, t);
    if (Object.keys(payload).length === 0) return;

    // Include the medicalHistory ID so the API updates the existing record
    const medicalHistoryId = patient?.medicalHistory?.id;
    const payloadWithMedicalHistoryId = payload as DiseaseHistoryPatch;
    if (payloadWithMedicalHistoryId.medicalHistory && medicalHistoryId) {
      payloadWithMedicalHistoryId.medicalHistory.id = medicalHistoryId;
    }

    try {
      await mutateAsync(payloadWithMedicalHistoryId);
    } catch {}
  }, [state, mutateAsync, patient, t]);

  const resetState = useCallback(() => setState(initialState), []);

  return (
    <DiseaseHistoryContext.Provider
      value={{ state, updateState, resetState, save }}
    >
      {children}
    </DiseaseHistoryContext.Provider>
  );
}

export function useDiseaseHistory() {
  const ctx = useContext(DiseaseHistoryContext);
  if (!ctx) {
    throw new Error(
      "useDiseaseHistory must be used within DiseaseHistoryProvider",
    );
  }
  return ctx;
}
